import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Globe, LayoutDashboard, List, Clock, TestTube2, Flame, X, Settings, Bug, Sun, Moon, PanelLeftClose } from 'lucide-react';
import ReviewIcon from './icons/ReviewIcon';
import AppLogo from './AppLogo';
import useMediaQuery from '../hooks/useMediaQuery';
import useHistoryBack from '../hooks/useHistoryBack';
import usePresence from '../hooks/usePresence';
import useModalLayer from '../hooks/useModalLayer';
import useDragDismiss from '../hooks/useDragDismiss';

const RAIL_KEY = 'nav_collapsed_v1';

/**
 * The app's frame on the left.
 *
 * One nav list, rendered into two shells: a `.sidebar` column inside the app
 * grid on desktop, and a `.side-panel` drawer under 900px, where the tab bar
 * carries primary navigation and this holds everything else.
 *
 * On desktop it collapses to a 72px rail. That is one CSS class on <body> —
 * the shell is a grid, so the column width and every label, count and heading
 * inside it respond to the same switch. The collapse control moves with it:
 * beside the logo when expanded, and a tab on the rail's outer edge when
 * collapsed, because the sidebar scrolls and would clip anything hanging off it.
 */
const SideDrawer = ({ isOpen, onClose, onOpenLocalizationModal, onOpenBugReport, theme, toggleTheme, onSignOut, isDemoMode, currentUser, setCurrentPage, currentPage, isPro = false, planReady = true, estimateCount = 0 }) => {
    const { t, i18n } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 900px)');
    const wrapRef = useRef(null);
    const panelRef = useRef(null);

    const [isRail, setIsRail] = useState(() => {
        try { return localStorage.getItem(RAIL_KEY) === '1'; } catch (e) { return false; }
    });

    // The rail is a body class because the grid column, the labels, the counts
    // and the footer all react to it — scoping it to the sidebar would mean
    // duplicating the switch in the layout.
    useEffect(() => {
        document.body.classList.toggle('nav-collapsed', isRail && !isMobile);
        return () => document.body.classList.remove('nav-collapsed');
    }, [isRail, isMobile]);

    const toggleRail = useCallback(() => {
        setIsRail(prev => {
            const next = !prev;
            try { localStorage.setItem(RAIL_KEY, next ? '1' : '0'); } catch (e) { /* private mode */ }
            return next;
        });
    }, []);

    // Handle system back button on mobile
    useHistoryBack(isOpen && isMobile, onClose, true, 'sideDrawer');

    const [isMounted, isAnimating] = usePresence(isOpen, 320);
    useModalLayer(isMobile && isOpen && isMounted, onClose, wrapRef, panelRef);
    const onGrab = useDragDismiss(panelRef, onClose, { enabled: isMobile });

    if (isMobile && !isMounted) {
        return null;
    }

    const handleNavClick = (page) => {
        setCurrentPage(page);
        if (isMobile) {
            onClose();
        }
    };

    const handleBugReportClick = () => {
        if (typeof onOpenBugReport === 'function') {
            onOpenBugReport();
        }
        if (isMobile) {
            onClose();
        }
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // The demo user's name is resolved here, not in useAuth, so it follows a language switch.
    const userDisplayName = currentUser?.isDemo
        ? t('demo.userName')
        : currentUser?.displayName || currentUser?.email || t('header.user');
    const userInitials = getInitials(userDisplayName);

    const PAGES = [
        { id: 'dashboard', label: t('header.dashboard'), icon: <LayoutDashboard /> },
        { id: 'portfolio', label: t('header.portfolio'), icon: <List /> },
        { id: 'timeline', label: t('header.timeline'), icon: <Clock /> },
        { id: 'simulations', label: t('header.simulations'), icon: <TestTube2 /> },
        { id: 'fire', label: t('header.fire'), icon: <Flame /> },
        // F28.1 — the review page is always listed: a menu that changes shape is
        // disorienting, and the count is what signals there is something waiting.
        // It counts estimates only, never the whole review list, or it would sit
        // there permanently nagging about work nobody asked for.
        { id: 'review', label: t('header.review'), icon: <ReviewIcon />, count: estimateCount },
    ];

    const planLabel = isDemoMode
        ? t('auth.demoMode', 'Demo')
        : !planReady
            ? '…'
            : isPro
                ? t('billing.proBadge', 'Pro')
                : t('billing.freeBadge', 'Free');

    // On a phone this is the Menu tab's sheet: the tab bar already lists the
    // first five destinations, so the sheet holds only what it doesn't.
    const navPages = isMobile ? PAGES.filter(p => p.id === 'review') : PAGES;

    const navList = (
        <nav className="nav-menu">
            {navPages.map(p => (
                <button
                    key={p.id}
                    onClick={() => handleNavClick(p.id)}
                    className={`nav-item ${currentPage === p.id ? 'active' : ''}`}
                    title={p.label}
                    // The rail turns this into a dot on the icon, which is the
                    // only way the count survives losing its label.
                    data-count={p.count > 0 ? p.count : undefined}
                    aria-current={currentPage === p.id ? 'page' : undefined}
                >
                    {p.icon}
                    <span>{p.label}</span>
                    {p.count > 0 && <span className="nav-count">{p.count}</span>}
                </button>
            ))}
            {/* Annotation pass 2: Settings left this list for the footer's icon
                row, beside theme and language — the three controls that are
                about the app rather than about the plan. With it gone the
                "Account" group had one member and then none, so it went too. */}
        </nav>
    );

    const logo = (
        <div className="logo">
            <AppLogo />
            <span className="logo-name">{t('auth.appName')}</span>
        </div>
    );

    const footer = (
        <div className="sidebar-foot">
            <div className="foot-actions">
                <button
                    onClick={() => handleNavClick('settings')}
                    className={`icon-btn ${currentPage === 'settings' ? 'on' : ''}`}
                    aria-label={t('header.settings')}
                    title={t('header.settings')}
                    aria-current={currentPage === 'settings' ? 'page' : undefined}
                >
                    <Settings />
                </button>
                {/* Always present, as the demo's own footer has it. It used to
                    be hidden in demo mode, which is exactly where someone
                    looking for it would be — and the report hook already answers
                    a demo submission with "not available in demo mode", so the
                    button explains itself rather than vanishing. */}
                <button
                    onClick={handleBugReportClick}
                    className="icon-btn"
                    aria-label={t('drawer.reportBug')}
                    title={t('drawer.reportBug')}
                >
                    <Bug />
                </button>
                <button
                    className="icon-btn"
                    onClick={toggleTheme}
                    aria-label={t(theme === 'light' ? 'drawer.darkMode' : 'drawer.lightMode')}
                    title={t(theme === 'light' ? 'drawer.darkMode' : 'drawer.lightMode')}
                >
                    {theme === 'light' ? <Moon /> : <Sun />}
                </button>
                <button
                    className="icon-btn"
                    onClick={onOpenLocalizationModal}
                    aria-label={t(`localization.languageName.${i18n.language.split('-')[0]}`, i18n.language.toUpperCase())}
                    title={t(`localization.languageName.${i18n.language.split('-')[0]}`, i18n.language.toUpperCase())}
                >
                    <Globe />
                </button>
            </div>
            {currentUser && (
                // Who you are, and the two things you do from here.
                // Sign-out keeps its own hit area beside the identity block.
                <div className="user-row">
                    {/* Post-release review: the identity block is a control
                        again. Pass 3 had made it inert on the argument that a
                        row saying who you are is not a button — but it sits
                        where every app puts the account menu, and the only way
                        in was the gear icon in the row above. The whole block
                        is the hit area now. The gear stays: collapsed to the
                        rail this row is just an avatar, and the icon row is
                        what still carries a labelled target. */}
                    <button
                        type="button"
                        className="user-row-open"
                        onClick={() => handleNavClick('settings')}
                        aria-label={t('header.settings')}
                        title={t('header.settings')}
                        aria-current={currentPage === 'settings' ? 'page' : undefined}
                    >
                        <div className="avatar">{userInitials}</div>
                        <div className="who">
                            <b>{userDisplayName}</b>
                            <span className={!isDemoMode && isPro ? 'is-pro' : undefined}>{planLabel}</span>
                        </div>
                    </button>
                    <button className="icon-btn sm" onClick={onSignOut} aria-label={isDemoMode ? t('auth.endDemo') : t('auth.signOut')}>
                        <LogOut />
                    </button>
                </div>
            )}
        </div>
    );

    if (isMobile) {
        return createPortal(
            <div ref={wrapRef} className={`side-wrap ${isAnimating ? 'open' : ''}`}>
                <div className="side-backdrop" onClick={onClose} aria-hidden="true" />
                <div
                    ref={panelRef}
                    className="side-panel"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('general.menu', 'Menu')}
                    onPointerDown={(e) => { if (e.target.closest('.sidebar-head')) onGrab(e); }}
                >
                    <button className="icon-btn side-close" onClick={onClose} aria-label={t('general.close')}>
                        <X size={20} />
                    </button>
                    <div className="sidebar-head">{logo}</div>
                    {navList}
                    {footer}
                </div>
            </div>,
            document.body
        );
    }

    return (
        <>
            <aside className="sidebar">
                <div className="sidebar-head">
                    {logo}
                    <button
                        className="icon-btn sm rail-toggle"
                        onClick={toggleRail}
                        aria-label={t('drawer.collapseNav', 'Collapse sidebar')}
                        title={t('drawer.collapseNav', 'Collapse sidebar')}
                    >
                        <PanelLeftClose />
                    </button>
                </div>
                {navList}
                {footer}
            </aside>
            {/* A sibling, not a child: the sidebar scrolls, so a tab hanging off
                its edge would be clipped by its own overflow. */}
            {/* The demo paints this tab with its own `collapse` glyph — a
                chevron *and* the panel's edge bar — and `shell.css` rotates the
                svg 180° so it reads as "open the panel". A plain ChevronRight
                came out of that rotation pointing left, i.e. at the rail it
                would be opening away from. PanelLeftClose is the same glyph the
                demo uses, and the same one `.rail-toggle` above uses. */}
            <button
                className="rail-tab"
                onClick={toggleRail}
                aria-label={t('drawer.expandNav', 'Expand sidebar')}
                title={t('drawer.expandNav', 'Expand sidebar')}
            >
                <PanelLeftClose />
            </button>
        </>
    );
};

export default SideDrawer;
